import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Container,
  Grid,
  Stack,
  Typography,
  Skeleton,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  HelpOutline as HelpIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";

// Blockchain Services & Web3
import cinstance from "../../../Service/randomcinstance";
import web3 from "../../../Service/web3";
import { networkID } from "../../../../static/utils";
import { getNetworkCurrency } from "../../../../static/Constants";

// Theme & Common UI
import NavBar from "../../../Components/NavBar";
import { colors, borderRadiusPx } from "../../../../theme";
import {
  CommonInput,
  CommonSelect,
  CommonButton,
  CommonChip,
  NoData,
} from "../../../Components/ui";

// Subcomponents
import SpinHeroBanner from "./components/SpinHeroBanner";
import SpinWheelCard from "./components/SpinWheelCard";
import "./SpinnerLotteryStyle.css";

const SORT_OPTIONS = [
  { value: "newest", label: "Newest First" },
  { value: "oldest", label: "Oldest First" },
  { value: "highestPrize", label: "Highest Prize Pool" },
  { value: "lowestEntry", label: "Lowest Entry Fee" },
  { value: "capacityHigh", label: "Most Slots" },
  { value: "capacityLow", label: "Fewest Slots (Higher Odds)" },
];

const withTimeout = (promise, ms = 4000) => {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error("RPC Request Timeout")), ms)
    ),
  ]);
};

const SpinnerLottery = () => {
  const [loading, setLoading] = useState(true);
  const [lotteries, setLotteries] = useState([]);
  const [partnerMap, setPartnerMap] = useState({});
  const [partnerLogoData, setPartnerLogoData] = useState([]);
  const [account, setAccount] = useState("");
  const [networkId, setNetworkId] = useState(networkID);

  // Filters & Search
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [statusFilter, setStatusFilter] = useState("all"); // "all" | "open" | "rollover"

  const apiUrl = process.env.REACT_APP_API_KEY;
  const currency = useMemo(() => getNetworkCurrency(networkId), [networkId]);

  // Check Account & Network
  const checkAccount = useCallback(async () => {
    if (window.ethereum) {
      try {
        window.web3 = web3;
        const netId = window.ethereum.networkVersion || (await withTimeout(web3.eth.net.getId(), 2500).catch(() => null));
        if (netId) {
          setNetworkId(netId);
          console.log("[SpinBET Page] Network ID detected:", netId);
        }

        const accounts = await withTimeout(web3.eth.getAccounts(), 2500).catch(() => []);
        console.log("[SpinBET Page] Detected connected accounts:", accounts);
        if (accounts && accounts.length > 0) {
          setAccount(accounts[0]);
          console.log("[SpinBET Page] Active wallet account:", accounts[0]);
        } else {
          console.log("[SpinBET Page] No connected account found.");
        }
      } catch (err) {
        console.error("[SpinBET Page] Error fetching accounts:", err);
      }
    }
  }, []);

  const connectWallet = async () => {
    console.log("[SpinBET Page] Initiating wallet connection request...");
    if (window.ethereum) {
      try {
        const accounts = await window.ethereum.request({
          method: "eth_requestAccounts",
        });
        if (accounts && accounts.length > 0) {
          setAccount(accounts[0]);
          console.log("[SpinBET Page] Wallet connected successfully:", accounts[0]);
          toast.success("Wallet connected successfully!");
        }
      } catch (err) {
        console.error("[SpinBET Page] Wallet connect failed:", err);
        toast.error("Failed to connect wallet.");
      }
    } else {
      console.warn("[SpinBET Page] MetaMask / Web3 provider not found in browser.");
      toast.error("Please install MetaMask to play SpinBET!");
    }
  };

  // Fetch all active SpinBET lotteries
  const fetchSpinLotteries = useCallback(async () => {
    setLoading(true);
    console.log("[SpinBET Page] Fetching active SpinBET lotteries from contract...");
    try {
      if (window.ethereum) window.web3 = web3;

      const totalIdsRes = await withTimeout(cinstance.methods.lotteryId().call(), 4000).catch((err) => {
        console.warn("[SpinBET Page] lotteryId() query timed out or failed:", err);
        return 1;
      });
      const totalIds = Number(totalIdsRes) || 1;
      console.log(`[SpinBET Page] Total lotteries registered on-chain: ${totalIds - 1}`);
      const lotteryPromises = [];

      for (let i = 1; i < totalIds; i++) {
        lotteryPromises.push(
          withTimeout(cinstance.methods.lottery(i).call(), 3500).catch(() => null)
        );
      }

      const results = (await Promise.all(lotteryPromises)).filter(Boolean);
      console.log({results});
      // Filter active SpinBET lotteries (Type 0, status 0, 3 or 5)
      const spinList = results.filter(
        (item) =>
          Number(item.lotteryType) === 0 &&
          (item.status === "0" || item.status === "3" || item.status === "5")
      );

      console.log(`[SpinBET Page] Found ${spinList.length} active SpinBET roulette wheels:`, spinList);
      setLotteries(spinList);

      // Load partner profiles in parallel for valid non-zero addresses only
      const partnerAddresses = [
        ...new Set(
          spinList
            .map((l) => l.partnerAddress)
            .filter(
              (addr) =>
                addr &&
                addr !== "0x0000000000000000000000000000000000000000" &&
                addr !== "0x0"
            )
        ),
      ];

      if (partnerAddresses.length > 0) {
        console.log(`[SpinBET Page] Loading partner info for ${partnerAddresses.length} unique creators...`);
        const partnerPromises = partnerAddresses.map((addr) =>
          withTimeout(cinstance.methods.partnerbyaddr(addr).call(), 3000)
            .then((res) => ({ [addr]: res }))
            .catch(() => ({ [addr]: null }))
        );

        const partnerEntries = await Promise.all(partnerPromises);
        const mergedPartnerMap = partnerEntries.reduce(
          (acc, curr) => ({ ...acc, ...curr }),
          {}
        );
        setPartnerMap(mergedPartnerMap);
        console.log("[SpinBET Page] Partner map loaded:", mergedPartnerMap);
      } else {
        setPartnerMap({});
      }

    } catch (err) {
      console.error("[SpinBET Page] Error loading SpinBET lotteries:", err);
      toast.error("Error loading on-chain roulette wheels.");
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch S3 Partner Logos
  useEffect(() => {
    let isMounted = true;
    if (apiUrl) {
      axios
        .get(`${apiUrl}/s3imageupload/getAllPartnerLogos`)
        .then((res) => {
          if (isMounted) {
            setPartnerLogoData(res?.data?.result || res?.data || []);
          }
        })
        .catch(() => {
          if (isMounted) setPartnerLogoData([]);
        });
    }
    return () => {
      isMounted = false;
    };
  }, [apiUrl]);

  useEffect(() => {
    checkAccount();
    fetchSpinLotteries();

    if (window.ethereum) {
      const handleAccounts = (accs) => {
        if (accs.length > 0) setAccount(accs[0]);
        else setAccount("");
      };
      window.ethereum.on("accountsChanged", handleAccounts);
      return () => {
        if (window.ethereum.removeListener) {
          window.ethereum.removeListener("accountsChanged", handleAccounts);
        }
      };
    }
  }, [checkAccount, fetchSpinLotteries]);

  // Combined Jackpot & Stats Calculations
  const combinedJackpot = useMemo(() => {
    let sumWei = 0;
    lotteries.forEach((l) => {
      try {
        const val = Number(web3.utils.fromWei(`${l.totalPrize || 0}`, "ether"));
        if (!isNaN(val)) sumWei += val;
      } catch {
        // ignore
      }
    });
    return sumWei.toFixed(2);
  }, [lotteries]);

  // Filtered and Sorted list
  const filteredLotteries = useMemo(() => {
    let list = [...lotteries];

    // Status filter
    if (statusFilter === "open") {
      list = list.filter((l) => l.status === "0" || l.status === "3");
    } else if (statusFilter === "rollover") {
      list = list.filter((l) => l.status === "5");
    }

    // Search query filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter((l) => {
        const idMatch = `${l.lotteryId}`.includes(q);
        const partner = partnerMap[l.partnerAddress];
        const nameMatch = partner?.name?.toLowerCase().includes(q) || partner?.companyName?.toLowerCase().includes(q);
        return idMatch || nameMatch;
      });
    }

    // Sorting
    list.sort((a, b) => {
      const aPrize = Number(a.totalPrize || 0);
      const bPrize = Number(b.totalPrize || 0);
      const aFee = Number(a.entryFee || 0);
      const bFee = Number(b.entryFee || 0);
      const aCap = Number(a.capacity || 0);
      const bCap = Number(b.capacity || 0);

      switch (sortBy) {
        case "oldest":
          return Number(a.lotteryId) - Number(b.lotteryId);
        case "highestPrize":
          return bPrize - aPrize;
        case "lowestEntry":
          return aFee - bFee;
        case "capacityHigh":
          return bCap - aCap;
        case "capacityLow":
          return aCap - bCap;
        case "newest":
        default:
          return Number(b.lotteryId) - Number(a.lotteryId);
      }
    });

    return list;
  }, [lotteries, statusFilter, searchQuery, sortBy, partnerMap]);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc" }}>
      <ToastContainer position="top-right" autoClose={3000} theme="light" />
      <NavBar />

      <Container maxWidth="xl" className="spinner-page-container" sx={{ pt: { xs: "90px", md: "100px" }, pb: 8 }}>
        {/* Hero Header & Live Stats Banner */}
        <SpinHeroBanner
          totalActive={lotteries.length}
          combinedPrize={combinedJackpot}
          currency={currency}
        />

        {/* Filter, Search & Sorting Controls Bar */}
        <Box
          sx={{
            bgcolor: "#ffffff",
            borderRadius: borderRadiusPx.lg,
            border: `1px solid ${colors.slate[200]}`,
            p: { xs: 2, md: 2.5 },
            mb: 4,
            boxShadow: "0 2px 8px rgba(0,0,0,0.02)",
          }}
        >
          <Grid container spacing={2} alignItems="center" justifyContent="space-between">
            {/* Search Field */}
            <Grid item xs={12} md={4}>
              <CommonInput
                placeholder="Search by Wheel #ID or Creator name..."
                value={searchQuery}
                onChange={(e) => {
                  console.log("[SpinBET Page] Search query changed:", e.target.value);
                  setSearchQuery(e.target.value);
                }}
                startAdornment={
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: colors.slate[400], fontSize: 20 }} />
                  </InputAdornment>
                }
                fullWidth
              />
            </Grid>

            {/* Status Tabs */}
            <Grid item xs={12} sm={6} md={4}>
              <Stack direction="row" spacing={1} alignItems="center">
                <CommonChip
                  label={`All (${lotteries.length})`}
                  variant={statusFilter === "all" ? "active" : "neutral"}
                  onClick={() => {
                    console.log("[SpinBET Page] Status filter changed -> 'all'");
                    setStatusFilter("all");
                  }}
                  clickable
                />
                <CommonChip
                  label="Open Wheels"
                  variant={statusFilter === "open" ? "active" : "neutral"}
                  onClick={() => {
                    console.log("[SpinBET Page] Status filter changed -> 'open'");
                    setStatusFilter("open");
                  }}
                  clickable
                />
                <CommonChip
                  label="Rollover Open"
                  variant={statusFilter === "rollover" ? "warning" : "neutral"}
                  onClick={() => {
                    console.log("[SpinBET Page] Status filter changed -> 'rollover'");
                    setStatusFilter("rollover");
                  }}
                  clickable
                />
              </Stack>
            </Grid>

            {/* Sorting & Refresh */}
            <Grid item xs={12} sm={6} md={4}>
              <Stack direction="row" spacing={1.5} justifyContent={{ xs: "flex-start", md: "flex-end" }} alignItems="center">
                <CommonSelect
                  value={sortBy}
                  onChange={(e) => {
                    console.log("[SpinBET Page] Sort option changed ->", e.target.value);
                    setSortBy(e.target.value);
                  }}
                  options={SORT_OPTIONS}
                  size="small"
                  sx={{ minWidth: 180 }}
                />
                <CommonButton
                  variant="outline"
                  size="small"
                  onClick={() => {
                    console.log("[SpinBET Page] Manual refresh triggered by user.");
                    fetchSpinLotteries();
                  }}
                  disabled={loading}
                  sx={{ p: 1, minWidth: 40 }}
                >
                  <RefreshIcon sx={{ fontSize: 18, animation: loading ? "spin 1s linear infinite" : undefined }} />
                </CommonButton>
              </Stack>
            </Grid>
          </Grid>
        </Box>

        {/* Live Wheels Grid or Loading Skeletons */}
        {loading ? (
          <Grid container spacing={3}>
            {[1, 2, 3, 4, 5, 6].map((idx) => (
              <Grid item xs={12} sm={6} lg={4} key={`skel-${idx}`}>
                <Box
                  sx={{
                    bgcolor: "#ffffff",
                    borderRadius: borderRadiusPx.xxl,
                    border: `1px solid ${colors.slate[200]}`,
                    p: 3,
                    height: 520,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Skeleton variant="circular" width={40} height={40} />
                    <Box sx={{ flexGrow: 1 }}>
                      <Skeleton variant="text" width="60%" height={24} />
                      <Skeleton variant="text" width="30%" height={16} />
                    </Box>
                  </Stack>
                  <Skeleton variant="circular" width={220} height={220} sx={{ mx: "auto", my: 2 }} />
                  <Skeleton variant="rounded" height={60} sx={{ borderRadius: borderRadiusPx.md }} />
                  <Skeleton variant="rounded" height={44} sx={{ borderRadius: borderRadiusPx.md }} />
                </Box>
              </Grid>
            ))}
          </Grid>
        ) : filteredLotteries.length === 0 ? (
          <Box sx={{ my: 6 }}>
            <NoData
              message={
                searchQuery
                  ? "No SpinBET wheels match your search criteria."
                  : "No active SpinBET roulette wheels currently open. Check back soon!"
              }
            />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {filteredLotteries.map((lottery) => {
              const partnerInfo = partnerMap[lottery.partnerAddress];
              return (
                <Grid item xs={12} sm={6} lg={4} key={`wheel-${lottery.lotteryId}`}>
                  <SpinWheelCard
                    lotteryData={lottery}
                    partner={partnerInfo}
                    partnerLogo={partnerLogoData}
                    account={account}
                    currency={currency}
                    onConnectWallet={connectWallet}
                  />
                </Grid>
              );
            })}
          </Grid>
        )}

        {/* Game Rules & FAQ Drawer */}
        <Box sx={{ mt: 8 }}>
          <Accordion
            sx={{
              borderRadius: `${borderRadiusPx.lg} !important`,
              border: `1px solid ${colors.slate[200]}`,
              boxShadow: "none",
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <HelpIcon sx={{ color: colors.primary.main, fontSize: 22 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: colors.slate[900] }}>
                  How does SpinBET Revolver work?
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, pb: 3, px: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Box sx={{ p: 2, bgcolor: colors.slate[50], borderRadius: borderRadiusPx.md, height: "100%" }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: colors.primary.main, mb: 0.5 }}>
                      1. Pick Your Segment
                    </Typography>
                    <Typography variant="body2" sx={{ color: colors.slate[600], lineHeight: 1.6 }}>
                      Select any number from 1 to the wheel capacity (e.g. 1-10 or 1-20), or use <strong>Lucky Pick</strong> to randomize your choice.
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ p: 2, bgcolor: colors.slate[50], borderRadius: borderRadiusPx.md, height: "100%" }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: colors.primary.main, mb: 0.5 }}>
                      2. Spin & Confirm On-Chain
                    </Typography>
                    <Typography variant="body2" sx={{ color: colors.slate[600], lineHeight: 1.6 }}>
                      Submit your entry fee via MetaMask. The smart contract generates a verifiable random draw using on-chain randomness.
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ p: 2, bgcolor: colors.slate[50], borderRadius: borderRadiusPx.md, height: "100%" }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: colors.primary.main, mb: 0.5 }}>
                      3. Instant Win & Payout
                    </Typography>
                    <Typography variant="body2" sx={{ color: colors.slate[600], lineHeight: 1.6 }}>
                      If the wheel lands on your selected number, the entire accumulated jackpot is automatically transferred to your wallet instantly!
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Box>
      </Container>
    </Box>
  );
};

export default SpinnerLottery;
